import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx-js-style";
import IconButton from "../../../DesignSystem/Buttons/IconButton";
import SearchField from "../../../DesignSystem/Inputs/SearchField";
import Tabs from "../../../DesignSystem/Tabs/Tabs";
import Tooltip from "../../../DesignSystem/Tooltip/Tooltip";
import { ReactComponent as DownloadIcon } from "../../../../assets/icons/download.svg";
import { ReactComponent as SortIcon } from "../../../../assets/icons/paraplanningDashboard/Sort.svg";
import { ReactComponent as InfoIcon } from "../../../../assets/icons/information.svg";
import {
  subscribeIOPaymentFlags,
} from "../services/ioPaymentFlagService";
import { formatStatementDate, statementDateParts } from "../../FCIStatements/statementDate";

const formatDateTime = (value) => {
  const date = value?.toDate?.() ||
    (typeof value?.seconds === "number"
      ? new Date(value.seconds * 1000)
      : value ? new Date(value) : null);
  return !date || Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleString("en-GB");
};
const formatPaymentDate = (value) => formatStatementDate(value) || "-";
const StatementDate = ({ value }) => {
  const parts = statementDateParts(value);
  return (
    <span className="fit-column" style={{ whiteSpace: "nowrap", minWidth: "max-content" }}>
      <span>{parts.date || "-"}</span>
      {parts.time ? <span>{parts.time}</span> : null}
    </span>
  );
};
const money = (value) => value === null || value === undefined
  ? "-"
  : Number(value).toLocaleString("en-GB", {
      style: "currency",
      currency: "GBP",
    });
const listValue = (value) => (Array.isArray(value) ? value : [value])
  .map((item) => String(item ?? "").trim())
  .filter(Boolean)
  .join(", ") || "-";
const isUnknownPolicyNumber = (value) => {
  const policyNumber = String(value ?? "").trim().toLowerCase();
  return !policyNumber || policyNumber === "-" || policyNumber.includes("unknown");
};

const COLUMNS = [
  { key: "paymentEntry", label: "Payment Entry", value: (flag) => listValue(flag.paymentIds || flag.paymentId) },
  { key: "recipient", label: "Recipient", value: (flag) => listValue(flag.recipients || flag.recipient) },
  { key: "paymentCreated", label: "Payment Created", value: (flag) => formatPaymentDate(flag.paymentCreated), sortValue: (flag) => flag.paymentCreated?.toDate?.()?.getTime?.() || (typeof flag.paymentCreated?.seconds === "number" ? flag.paymentCreated.seconds * 1000 : new Date(flag.paymentCreated || 0).getTime()) },
  { key: "adviser", label: "Selling Adviser", value: (flag) => flag.policyAdviser || "-" },
  { key: "iob", label: "IOB", value: (flag) => listValue(flag.iobReferences || flag.iobReference) },
  { key: "client", label: "Client Name", value: (flag) => flag.clientName || "-" },
  { key: "policy", label: "Policy Number", value: (flag) => flag.policyAccountNumber || "-" },
  { key: "provider", label: "Policy Provider", value: (flag) => flag.policyProvider || "-" },
  { key: "paymentAmount", label: "Payment Amount", value: (flag) => money(flag.paymentAmount), sortValue: (flag) => Number(flag.paymentAmount || 0) },
  { key: "gross", label: "Gross Received", value: (flag) => money(flag.grossReceived), sortValue: (flag) => Number(flag.grossReceived || 0) },
];

const columnClassName = (key) => [
  key === "paymentEntry" ? "io-flags-payment-entry-column" : "",
  key === "recipient" ? "io-flags-recipient-column" : "",
  ["iob", "client", "policy", "provider"].includes(key) ? `io-flags-${key}-column` : "",
  ["paymentAmount", "gross"].includes(key) ? "text-align-right" : "",
].filter(Boolean).join(" ");

const FlagSearch = ({ onSearchChange }) => {
  const [value, setValue] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => onSearchChange(value), 250);
    return () => window.clearTimeout(timer);
  }, [onSearchChange, value]);

  return (
    <SearchField
      id="io-payment-flags-search"
      value={value}
      onChange={(event) => setValue(event.target.value)}
      placeholder="Search flags"
    />
  );
};

const IOPaymentFlags = ({ selectedMonth, selectedAdviserId }) => {
  const [searchParams] = useSearchParams();
  const selectedFlagId = searchParams.get("flag") || "";
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [flagView, setFlagView] = useState("active");
  const [sort, setSort] = useState({ key: "paymentCreated", direction: -1 });

  useEffect(() => subscribeIOPaymentFlags(
    (items) => {
      setFlags(items);
      setLoading(false);
      setError("");
    },
    (loadError) => {
      setLoading(false);
      setError(loadError.message || "Unable to load IO payment flags.");
    },
  ), []);

  useEffect(() => {
    if (!selectedFlagId || !flags.some((flag) => flag.id === selectedFlagId)) return;
    document.getElementById(`io-payment-flag-${selectedFlagId}`)?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [flags, selectedFlagId]);

  const scopedFlags = useMemo(() => {
    const adviserIds = String(selectedAdviserId || "")
      .split(",")
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);
    return flags.filter((flag) => {
      if (selectedMonth && flag.paymentMonth !== selectedMonth) return false;
      if (!adviserIds.length) return true;
      const flagRecipientIds = (Array.isArray(flag.recipientCrmIds)
        ? flag.recipientCrmIds
        : [flag.recipientCrmId])
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean);
      return adviserIds.some((id) => flagRecipientIds.includes(id));
    });
  }, [flags, selectedAdviserId, selectedMonth]);
  const visibleFlags = useMemo(
    () => scopedFlags.filter((flag) => (
      flagView === "active"
        ? flag.detectionStatus === "active"
        : flag.detectionStatus !== "active"
    )),
    [flagView, scopedFlags],
  );
  const filteredFlags = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return visibleFlags;
    return visibleFlags.filter((flag) => [
      ...COLUMNS.map((column) => column.value(flag)),
      flag.reason,
      flag.flagType,
      flag.detectionStatus,
      formatDateTime(flag.lastSeenAt),
      formatDateTime(
        flag.serverUpdatedAt || flag.statusUpdatedAt || flag.sourceUpdatedAt,
      ),
      ...(flag.paymentIds || []),
    ].some((value) => String(value || "").toLowerCase().includes(query)));
  }, [search, visibleFlags]);
  const sortedFlags = useMemo(
    () => [...filteredFlags].sort((left, right) => {
      const column = COLUMNS.find((item) => item.key === sort.key);
      const leftValue = column?.sortValue?.(left) ?? column?.value(left) ?? "";
      const rightValue = column?.sortValue?.(right) ?? column?.value(right) ?? "";
      const comparison = typeof leftValue === "number" && typeof rightValue === "number"
        ? leftValue - rightValue
        : String(leftValue).localeCompare(String(rightValue), "en-GB", { numeric: true });
      return comparison * sort.direction;
    }),
    [filteredFlags, sort],
  );
  const sortBy = (key) => setSort((current) => ({
    key,
    direction: current.key === key ? -current.direction : 1,
  }));
  const downloadFlags = () => {
    const exportRows = sortedFlags.map((flag) => ({
      "Payment Entry": listValue(flag.paymentIds || flag.paymentId),
      Recipient: listValue(flag.recipients || flag.recipient),
      "Payment Created": formatPaymentDate(flag.paymentCreated),
      "Selling Adviser": flag.policyAdviser || "-",
      IOB: listValue(flag.iobReferences || flag.iobReference),
      "Client Name": flag.clientName || "-",
      "Policy Number": flag.policyAccountNumber || "-",
      "Policy Provider": flag.policyProvider || "-",
      "Payment Amount": Number(flag.paymentAmount || 0),
      "Gross Received": Number(flag.grossReceived || 0),
      Condition: flag.reason || flag.flagType || "-",
      "Last Verified": formatDateTime(flag.lastSeenAt),
      "Last Updated": formatDateTime(
        flag.serverUpdatedAt || flag.statusUpdatedAt || flag.sourceUpdatedAt,
      ),
    }));
    const sheet = XLSX.utils.json_to_sheet(exportRows);
    const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
    sheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };
    sheet["!cols"] = Object.keys(exportRows[0] || {}).map(() => ({ wch: 20 }));
    for (let column = range.s.c; column <= range.e.c; column += 1) {
      const header = sheet[XLSX.utils.encode_cell({ r: 0, c: column })];
      if (header) {
        header.s = {
          fill: { fgColor: { rgb: "081E56" } },
          font: { color: { rgb: "FFFFFF" }, bold: true },
        };
      }
    }
    ["Payment Amount", "Gross Received"].forEach((heading) => {
      const column = Object.keys(exportRows[0] || {}).indexOf(heading);
      if (column < 0) return;
      exportRows.forEach((_, row) => {
        const cell = sheet[XLSX.utils.encode_cell({ r: row + 1, c: column })];
        if (cell) cell.z = "£#,##0.00";
      });
    });
    const exportColumns = Object.keys(exportRows[0] || {});
    const iobColumn = exportColumns.indexOf("IOB");
    if (iobColumn >= 0) {
      sortedFlags.forEach((flag, row) => {
        if (!flag.planUrl) return;
        const cell = sheet[XLSX.utils.encode_cell({ r: row + 1, c: iobColumn })];
        if (!cell) return;
        cell.l = { Target: flag.planUrl, Tooltip: "Open plan in Intelliflo" };
        cell.s = {
          ...cell.s,
          font: { color: { rgb: "0563C1" }, underline: true },
        };
      });
    }
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "IO Flags");
    saveAs(
      new Blob([XLSX.write(workbook, { bookType: "xlsx", type: "array" })]),
      `io-${flagView === "active" ? "review-flags" : "inactive-flags"}-${selectedMonth || "all"}.xlsx`,
    );
  };

  return (
    <section className="website-dashboard-card card-corner-radius io-summary-table io-payment-flags-table fit-column gap-s" aria-label="IO payment flags">
      <header className="row align-center gap-s padding-s">
        <div className="row align-center gap-s">
          <h3 className="FP_H3">Review Flags</h3>
          <span className="FP_Content_Semibold text-primary-1" aria-label={`${filteredFlags.length} flags`}>
            ({filteredFlags.length.toLocaleString("en-GB")})
          </span>
          <IconButton
            icon={<DownloadIcon aria-hidden="true" />}
            onClick={downloadFlags}
            disabled={!filteredFlags.length}
            label={`Download ${flagView === "active" ? "review" : "inactive"} flags`}
          />
        </div>
      </header>
      <div className="io-flags-toolbar padding-s gap-m">
        <div className="io-flags-tabs">
          <Tabs
            tabs={[
              { id: "active", label: "Active" },
              { id: "inactive", label: "Inactive" },
            ]}
            activeTab={flagView}
            onTabChange={setFlagView}
            noShadow
          />
        </div>
        <div className="io-flags-search">
          <FlagSearch onSearchChange={setSearch} />
        </div>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? (
        <p role="status">Loading flags…</p>
      ) : filteredFlags.length === 0 ? (
        <p className="FP_Content padding-s">
          {search
            ? "No review flags match your search."
            : `No ${flagView === "active" ? "active" : "inactive"} IO payment flags match the selected period and adviser.`}
        </p>
      ) : (
        <div className="table-scroll-container">
          <table aria-label="IO payment flags">
            <thead>
              <tr>
                <th className="FP_Help_Text_Semibold uppercase white-text table-index-column">
                  Index
                </th>
                {COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    className={`FP_Help_Text_Semibold uppercase white-text ${columnClassName(column.key)}`}
                    aria-sort={sort.key === column.key
                      ? sort.direction === 1 ? "ascending" : "descending"
                      : "none"}
                  >
                    <button
                      type="button"
                      className={`table-heading-content table-sort-button ${["paymentAmount", "gross"].includes(column.key) ? "width-full justify-flex-end" : ""}`}
                      onClick={() => sortBy(column.key)}
                    >
                      {column.label}
                      <SortIcon aria-hidden="true" />
                    </button>
                  </th>
                ))}
                <th className="FP_Help_Text_Semibold uppercase white-text table-action-column">
                  Details
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedFlags.map((flag, index) => (
                <tr
                  key={flag.id}
                  id={`io-payment-flag-${flag.id}`}
                  aria-current={selectedFlagId === flag.id ? "true" : undefined}
                  className={selectedFlagId === flag.id ? "io-payment-flag-selected" : ""}
                >
                  <td className="FP_Help_Text text-black table-index-column">
                    {index + 1}
                  </td>
                  {COLUMNS.map((column) => (
                    <td
                      key={column.key}
                      className={`FP_Help_Text ${column.key === "policy" && isUnknownPolicyNumber(flag.policyAccountNumber) ? "text-error" : "text-black"} ${columnClassName(column.key)}`}
                    >
                      {column.key === "paymentEntry" && flag.paymentIds?.length ? (
                        <span className="fit-column gap-xs">
                          {flag.paymentIds.map((paymentId) => (
                            <span key={String(paymentId)}>{String(paymentId)}</span>
                          ))}
                        </span>
                      ) : column.key === "recipient" && flag.recipientPayments?.length ? (
                        <span className="fit-column gap-xs">
                          {flag.recipientPayments.map((item, itemIndex) => (
                            <span key={`${item.recipient || "recipient"}-${itemIndex}`}>
                              {item.recipient || "-"}
                            </span>
                          ))}
                        </span>
                      ) : column.key === "paymentAmount" && flag.recipientPayments?.length ? (
                        <span className="fit-column gap-xs">
                          {flag.recipientPayments.map((item, itemIndex) => (
                            <span key={`${item.recipient || "payment"}-${itemIndex}`}>
                              {money(item.paymentAmount)}
                            </span>
                          ))}
                        </span>
                      ) : column.key === "paymentCreated" ? (
                        <StatementDate value={flag.paymentCreated} />
                      ) : column.key === "client" && flag.clientUrl ? (
                        <a className="table-url-link text-primary-1" href={flag.clientUrl} target="_blank" rel="noreferrer">
                          {column.value(flag)}
                        </a>
                      ) : column.key === "iob" && flag.planUrl ? (
                        <a
                          className="table-url-link text-primary-1"
                          href={flag.planUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {column.value(flag)}
                        </a>
                      ) : column.key === "policy" && flag.planUrl ? (
                        <a
                          className={`table-url-link ${isUnknownPolicyNumber(flag.policyAccountNumber) ? "text-error" : "text-primary-1"}`}
                          href={flag.planUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {column.value(flag)}
                        </a>
                      ) : column.value(flag)}
                    </td>
                  ))}
                  <td className="table-action-column">
                    <Tooltip
                      position="left"
                      content={(
                        <span className="fit-column gap-xs">
                          <span>
                            <strong>Condition:</strong>{" "}
                            {flag.reason || flag.flagType || "-"}
                          </span>
                          <span>
                            <strong>Detection:</strong>{" "}
                            {flag.detectionStatus === "active"
                              ? "Active"
                              : "Cleared"}
                          </span>
                          <span>
                            <strong>Last verified:</strong>{" "}
                            {formatDateTime(flag.lastSeenAt)}
                          </span>
                          <span>
                            <strong>Last updated:</strong>{" "}
                            {formatDateTime(
                              flag.serverUpdatedAt ||
                                flag.statusUpdatedAt ||
                                flag.sourceUpdatedAt,
                            )}
                          </span>
                        </span>
                      )}
                    >
                      <span tabIndex={0} aria-label="View flag condition and timestamps">
                        <InfoIcon width={20} height={20} aria-hidden="true" />
                      </span>
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default IOPaymentFlags;
